import { useState } from 'react';
import { dec } from '@app/shared';
import {
  App,
  Button,
  Checkbox,
  DatePicker,
  Flex,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { INVALID_INPUTS_HINT, InvalidInputsContext, MoneyInput, useInvalidInputs } from '@/shared/ui/inputs';
import { CounterpartySelect } from '@/entities/counterparty';
import { useListDocumentsQuery } from '@/entities/document';
import type { PaymentDirection, PaymentDto, PaymentMethod } from '@/entities/payment';
import {
  useCreateOffsetMutation,
  useCreatePaymentMutation,
  useDeletePaymentMutation,
  useGetPaymentQuery,
  useSetPaymentAllocationsMutation,
  useUpdatePaymentMutation,
} from '@/entities/payment';
import {
  usePaymentSaveWithPreview,
  PaymentPreviewConsequencesDialog,
} from '@/features/payment/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { useStoreContext } from '@/shared/lib/store-context';
import { formatDate, todayInTz } from '@/shared/lib/date';
import { translateApiError } from '@/shared/api/errorToMessage';
import type { AllocationRow, PaymentFormType } from './model';
import { allocationRows, allocationSeedKey, allocationTarget, offsetIsBalanced, rowsFromDocuments, sumAmounts, toManualAllocations } from './model';

const METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Наличные' },
  { value: 'CASHLESS', label: 'Безналичные' },
  { value: 'TRANSFER', label: 'Перевод' },
];

export interface PaymentWindowProps {
  id?: string;
  counterpartyId?: string;
  prefillDocumentId?: string;
  prefillAmount?: string;
  direction?: PaymentDirection;
  onClose: () => void;
}

/** §7: «Добавить оплату» / взаимозачёт. Разнесение — авто-FIFO, если строки грида не заполнены. */
export function PaymentWindow({
  id,
  counterpartyId: initialCounterpartyId,
  prefillDocumentId,
  prefillAmount,
  direction: initialDirection,
  onClose,
}: PaymentWindowProps) {
  const { message, modal } = App.useApp();
  const ctx = useStoreContext();
  const isNew = !id;

  const {
    data: existing,
    isFetching,
    refetch: refetchExisting,
  } = useGetPaymentQuery(id ?? '', { skip: !id });
  const [createPayment, { isLoading: isCreating }] = useCreatePaymentMutation();
  const [createOffset, { isLoading: isCreatingOffset }] = useCreateOffsetMutation();
  const [updatePayment, { isLoading: isUpdating }] = useUpdatePaymentMutation();
  const [setAllocations, { isLoading: isSettingAllocations }] = useSetPaymentAllocationsMutation();
  const [deletePayment, { isLoading: isDeleting }] = useDeletePaymentMutation();
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } =
    usePaymentSaveWithPreview();

  const [counterpartyId, setCounterpartyId] = useState(initialCounterpartyId);
  const [date, setDate] = useState(todayInTz(ctx.timezone));
  const [type, setType] = useState<PaymentFormType>('PAYMENT');
  const [direction, setDirection] = useState<PaymentDirection>(initialDirection ?? 'IN');
  const [amount, setAmount] = useState<string | null>(prefillAmount ?? null);
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [comment, setComment] = useState('');
  const [noAutoAllocate, setNoAutoAllocate] = useState(false);
  const [rows, setRows] = useState<AllocationRow[]>([]);
  // Пока владелец правит суммы в сетке, фоновое обновление данных её не пересевает.
  const [rowsDirty, setRowsDirty] = useState(false);
  const [receivableRows, setReceivableRows] = useState<AllocationRow[]>([]);
  const [payableRows, setPayableRows] = useState<AllocationRow[]>([]);

  function seedFrom(d: PaymentDto) {
    setCounterpartyId(d.counterpartyId);
    setDate(d.payDate.slice(0, 10));
    setType(d.type);
    setDirection(d.direction ?? 'IN');
    setAmount(d.amount);
    setMethod(d.method);
    setComment(d.comment ?? '');
    setNoAutoAllocate(d.doNotAllocate);
  }

  // Загруженный платёж → заполнить форму один раз (render-phase, не эффект).
  const [seededId, setSeededId] = useState<string | undefined>(undefined);
  if (existing && existing.id !== seededId) {
    setSeededId(existing.id);
    seedFrom(existing);
  }

  /** §8.7 «Параллельная правка»: VERSION_CONFLICT предлагает открыть свежую версию (отбрасывая
   * локальные правки) вместо тупикового тоста — тот же приём, что и в `DocumentWindow`. */
  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(translateApiError(e));
      return;
    }
    modal.confirm({
      title: 'Платёж изменён в другом окне',
      content:
        'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      onOk: async () => {
        const result = await refetchExisting();
        if (result.data) seedFrom(result.data);
      },
    });
  }

  const target = allocationTarget(type, direction);
  const { data: sideList } = useListDocumentsQuery(
    {
      type: target.documentType,
      counterpartyIds: counterpartyId ? [counterpartyId] : [],
      statuses: ['POSTED'],
      paymentStatuses: ['UNPAID', 'PARTIAL'],
    },
    { skip: !counterpartyId || type === 'OFFSET' },
  );
  const { data: receivableList } = useListDocumentsQuery(
    {
      type: 'SALE',
      counterpartyIds: counterpartyId ? [counterpartyId] : [],
      statuses: ['POSTED'],
      paymentStatuses: ['UNPAID', 'PARTIAL'],
    },
    { skip: !counterpartyId || type !== 'OFFSET' },
  );
  const { data: payableList } = useListDocumentsQuery(
    {
      type: 'PURCHASE',
      counterpartyIds: counterpartyId ? [counterpartyId] : [],
      statuses: ['POSTED'],
      paymentStatuses: ['UNPAID', 'PARTIAL'],
    },
    { skip: !counterpartyId || type !== 'OFFSET' },
  );

  // Сетка строится из того, что отдал сервер, и пересевается каждый раз, когда он отдал другое —
  // в том числе после сохранения (тот же набор документов, но другая оплата и версия платежа).
  const [seededSideKey, setSeededSideKey] = useState<string | undefined>(undefined);
  const sideKey = sideList ? allocationSeedKey(`${type}:${direction}`, existing?.rowVersion, sideList.items, existing?.allocations) : undefined;
  if (sideKey && sideKey !== seededSideKey && !rowsDirty) {
    setSeededSideKey(sideKey);
    setRows(allocationRows(sideList!.items, existing?.allocations ?? [], target, prefillDocumentId, amount));
  }

  function editRows(next: AllocationRow[]) {
    setRowsDirty(true);
    setRows(next);
  }
  const receivableKey = receivableList
    ? `r:${receivableList.items.map((i) => i.id).join(',')}`
    : undefined;
  const [seededReceivableKey, setSeededReceivableKey] = useState<string | undefined>(undefined);
  if (receivableKey && receivableKey !== seededReceivableKey) {
    setSeededReceivableKey(receivableKey);
    setReceivableRows(rowsFromDocuments(receivableList!.items, 'С'));
  }
  const payableKey = payableList ? `p:${payableList.items.map((i) => i.id).join(',')}` : undefined;
  const [seededPayableKey, setSeededPayableKey] = useState<string | undefined>(undefined);
  if (payableKey && payableKey !== seededPayableKey) {
    setSeededPayableKey(payableKey);
    setPayableRows(rowsFromDocuments(payableList!.items, 'П'));
  }

  const busy =
    isCreating ||
    isCreatingOffset ||
    isUpdating ||
    isSettingAllocations ||
    isDeleting ||
    isRunningPreview;
  // Сумма с опечаткой в строке разнесения уходит из поля как «пусто» — платёж сохранился бы с
  // автоматическим разнесением вместо введённого вручную.
  const invalidInputs = useInvalidInputs();
  const canSubmitOffset =
    type === 'OFFSET' ? offsetIsBalanced(amount ?? '', receivableRows, payableRows) : true;
  const canSubmit =
    !invalidInputs.hasInvalid &&
    !!counterpartyId &&
    !!date &&
    !!amount &&
    dec(amount).gt(0) &&
    (type !== 'OFFSET' || canSubmitOffset);

  async function handleCreate() {
    if (!counterpartyId || !amount || invalidInputs.hasInvalid) return;
    try {
      if (type === 'OFFSET') {
        await createOffset({
          counterpartyId,
          date,
          amount,
          comment: comment || undefined,
          receivable: toManualAllocations(receivableRows),
          payable: toManualAllocations(payableRows),
        }).unwrap();
      } else {
        // «Не разносить» — только у обычной оплаты: возврат денег авансом не бывает.
        const keepFree = type === 'PAYMENT' && noAutoAllocate;
        const manual = toManualAllocations(rows);
        await createPayment({
          counterpartyId,
          date,
          amount,
          direction,
          method,
          type,
          comment: comment || undefined,
          noAutoAllocate: keepFree,
          allocations: keepFree || manual.length === 0 ? undefined : manual,
        }).unwrap();
      }
      message.success('Платёж добавлен');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  async function handleSaveFields() {
    if (!id || !existing || invalidInputs.hasInvalid) return;
    const fields = { date, amount: amount ?? undefined, method, comment, noAutoAllocate };
    try {
      const saved = await runWithPreview(
        id,
        'update',
        existing.rowVersion,
        () => updatePayment({ id, body: { rowVersion: existing.rowVersion, ...fields } }).unwrap(),
        fields,
      );
      if (saved) {
        setRowsDirty(false);
        message.success('Сохранено');
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  async function handleSaveAllocations() {
    if (!id || !existing || invalidInputs.hasInvalid) return;
    const allocations = toManualAllocations(rows);
    // Галочка «Не разносить» уходит вместе с разнесением: раньше снятая на экране галочка не
    // передавалась, и сервер молча выбрасывал только что сохранённые строки.
    const body = { allocations, noAutoAllocate };
    try {
      const saved = await runWithPreview(
        id,
        'setAllocations',
        existing.rowVersion,
        () => setAllocations({ id, body: { rowVersion: existing.rowVersion, ...body } }).unwrap(),
        body,
      );
      if (saved) {
        setRowsDirty(false);
        message.success('Разнесение обновлено');
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  function handleDelete() {
    if (!id || !existing) return;
    modal.confirm({
      title: 'Удалить платёж?',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const deleted = await runWithPreview(id, 'remove', existing.rowVersion, () =>
            deletePayment({ id, body: { rowVersion: existing.rowVersion } }).unwrap(),
          );
          if (deleted) {
            message.success('Платёж удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  const isLinkedToDocument = !!existing?.createdFromDocumentId;
  const readOnlyHeader = !isNew && isLinkedToDocument;

  return (
    <>
      <PaymentPreviewConsequencesDialog diff={pendingDiff} onResolve={resolvePending} />
      <FloatingWindow
        kind="payment"
        title={isNew ? 'Новая оплата' : `Платёж от ${formatDate(date)}`}
        onClose={onClose}
        footer={renderFooter()}
      >
        <InvalidInputsContext.Provider value={invalidInputs.api}>
        <Flex vertical gap={8}>
          {isNew ? (
            <Segmented
              value={type}
              onChange={(v) => setType(v as PaymentFormType)}
              options={[
                { value: 'PAYMENT', label: 'Оплата' },
                { value: 'REFUND', label: 'Возврат денег' },
                { value: 'OFFSET', label: 'Взаимозачёт' },
              ]}
            />
          ) : null}

          <Flex gap={8} wrap>
            <Flex vertical style={{ minWidth: 220, flex: 1 }}>
              <Typography.Text style={{ fontSize: 12 }}>Контрагент</Typography.Text>
              <CounterpartySelect
                value={counterpartyId}
                current={existing?.counterparty}
                disabled={!isNew}
                onChange={setCounterpartyId}
              />
            </Flex>
            <Flex vertical style={{ minWidth: 140 }}>
              <Typography.Text style={{ fontSize: 12 }}>Дата</Typography.Text>
              <DatePicker
                value={dayjs(date)}
                format="DD.MM.YYYY"
                disabled={readOnlyHeader}
                onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : date)}
              />
            </Flex>
            {type !== 'OFFSET' && isNew ? (
              <Flex vertical style={{ minWidth: 160 }}>
                <Typography.Text style={{ fontSize: 12 }}>Направление</Typography.Text>
                <Segmented
                  value={direction}
                  onChange={(v) => setDirection(v as PaymentDirection)}
                  options={[
                    { value: 'IN', label: 'Получили' },
                    { value: 'OUT', label: 'Отдали' },
                  ]}
                />
              </Flex>
            ) : null}
          </Flex>

          <Flex gap={8} wrap>
            <Flex vertical style={{ minWidth: 140 }}>
              <Typography.Text style={{ fontSize: 12 }}>Сумма</Typography.Text>
              <MoneyInput value={amount} onChange={setAmount} />
            </Flex>
            {type !== 'OFFSET' ? (
              <Flex vertical style={{ minWidth: 160 }}>
                <Typography.Text style={{ fontSize: 12 }}>Способ</Typography.Text>
                <Select<PaymentMethod>
                  value={method}
                  disabled={readOnlyHeader}
                  onChange={setMethod}
                  options={METHOD_OPTIONS}
                  style={{ width: '100%' }}
                />
              </Flex>
            ) : null}
            <Flex vertical style={{ minWidth: 200, flex: 1 }}>
              <Typography.Text style={{ fontSize: 12 }}>Комментарий</Typography.Text>
              <Input value={comment} onChange={(e) => setComment(e.target.value)} />
            </Flex>
          </Flex>

          {type === 'PAYMENT' ? (
            <Checkbox
              checked={noAutoAllocate}
              disabled={readOnlyHeader}
              onChange={(e) => setNoAutoAllocate(e.target.checked)}
            >
              Не разносить (оставить свободным авансом)
            </Checkbox>
          ) : null}

          {type !== 'OFFSET' && !(type === 'PAYMENT' && noAutoAllocate) && counterpartyId ? (
            <AllocationGrid
              title={target.title}
              remainderTitle={target.remainderTitle}
              emptyText={type === 'REFUND' ? 'Нет незакрытых возвратов' : undefined}
              rows={rows}
              onChange={editRows}
            />
          ) : null}

          {type === 'OFFSET' && counterpartyId ? (
            <Flex gap={12} wrap>
              <div style={{ flex: 1, minWidth: 260 }}>
                <AllocationGrid
                  title={`Нам должны (${sumAmounts(receivableRows)})`}
                  rows={receivableRows}
                  onChange={setReceivableRows}
                />
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <AllocationGrid
                  title={`Мы должны (${sumAmounts(payableRows)})`}
                  rows={payableRows}
                  onChange={setPayableRows}
                />
              </div>
            </Flex>
          ) : null}

          {!isNew && existing ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {isLinkedToDocument
                ? 'Создан автоматически при проведении документа. Сумму можно изменить напрямую — дата, способ и разнесение управляются документом (чтобы изменить их, снимите проведение).'
                : null}
            </Typography.Text>
          ) : null}

          {invalidInputs.hasInvalid ? (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {INVALID_INPUTS_HINT}
            </Typography.Text>
          ) : null}
        </Flex>
        </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  function renderFooter() {
    if (isFetching && id) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;
    if (isNew) {
      return (
        <Space wrap>
          <Button
            type="primary"
            loading={busy}
            disabled={!canSubmit}
            onClick={() => void handleCreate()}
          >
            Добавить
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      );
    }
    return (
      <Space wrap>
        <Button
          type="primary"
          loading={busy}
          disabled={invalidInputs.hasInvalid || !amount || dec(amount).lte(0)}
          onClick={() => void handleSaveFields()}
        >
          Сохранить
        </Button>
        {!readOnlyHeader && type !== 'OFFSET' ? (
          <Button loading={busy} disabled={invalidInputs.hasInvalid} onClick={() => void handleSaveAllocations()}>
            Сохранить разнесение
          </Button>
        ) : null}
        {!isLinkedToDocument ? (
          <Button danger loading={busy} onClick={handleDelete}>
            Удалить
          </Button>
        ) : null}
        <Button onClick={onClose}>Закрыть</Button>
      </Space>
    );
  }
}

function AllocationGrid({
  title,
  remainderTitle = 'Долг',
  emptyText = 'Нет неоплаченных документов',
  rows,
  onChange,
}: {
  title: string;
  remainderTitle?: string;
  emptyText?: string;
  rows: AllocationRow[];
  onChange: (rows: AllocationRow[]) => void;
}) {
  return (
    <Flex vertical gap={4}>
      <Typography.Text style={{ fontSize: 12 }} strong>
        {title}
      </Typography.Text>
      <Table<AllocationRow>
        size="small"
        rowKey="documentId"
        dataSource={rows}
        pagination={false}
        locale={{ emptyText }}
        columns={[
          { title: 'Документ', dataIndex: 'label', width: 120 },
          { title: remainderTitle, dataIndex: 'debt', width: 90, align: 'right' },
          {
            title: 'Сумма',
            dataIndex: 'amount',
            width: 110,
            align: 'right',
            render: (_v, row) => (
              <MoneyInput
                value={row.amount}
                onChange={(v) =>
                  onChange(
                    rows.map((r) => (r.documentId === row.documentId ? { ...r, amount: v } : r)),
                  )
                }
              />
            ),
          },
        ]}
      />
    </Flex>
  );
}
